import React from 'react';
import { BaseUserPreferenceContextType, FrameworkProvider, DevMenu, Layouts } from '@ukpostbox/frontend-framework';
import { AuthProvider } from 'react-oauth2-code-pkce';
import { createBrowserRouter, Outlet, RouterProvider, ScrollRestoration } from 'react-router';
import { IPMReactQueryProvider } from '@ukpostbox/client-data';
import { Logo } from './_Global/Components/Logo';
import Navigation from './_Global/Components/Navigation/Navigation';
import { SiteDevMenu } from './_Global/Components/SiteDevMenu';
import { GlobalComponents } from './_Global/Components/GlobalComponents';
import ErrorPage from './_Global/Pages/ErrorPage';
import MessageCentreProvider from './MessageCentre/Providers/MessageCentreProvider';
import NewMailboxProvider from './Mailboxes/Providers/NewMailboxProvider';
import SiteInterfaceProvider from './_Global/Providers/SiteInterfaceProvider';
import AuthorisedUserProvider, { AuthorisedUserContext } from './_Auth/Providers/AuthProvider';
import UserProvider from './User/Providers/UserProvider';
import MailRoutes from './Mailboxes/MailRoutes';
import MessageCentreRoutes from './MessageCentre/MessageCentreRoutes';
import { PublicRoutes } from './_Global/PublicRoutes';
import SignUpRoutes from './SignUp/SignUpRoutes';
import TopRoutes from './_Global/TopRoutes';
import { Header } from './_Global/Components/Header';

import './_Global/Locales/i18n';
import './_Global/Styles/custom-styles.scss';
import { authConfig } from './_Auth/AuthSettings';

function Layout(props: any) {
	return (
		<IPMReactQueryProvider showDevTools={navigator.webdriver === false}>
			<AuthProvider authConfig={authConfig}>
				<AuthorisedUserProvider>
					<FrameworkProvider
						userPreferencesContext={
							AuthorisedUserContext as unknown as React.Context<BaseUserPreferenceContextType>
						}
						defaultLayout={Layouts.SIDEBAR_LAYOUT}
						headerComponent={<Header />}
						sidebarComponent={<Navigation logo={<Logo />} />}
						devComponent={
							process.env.REACT_APP_ENV_LABEL !== 'Production' && navigator.webdriver === false ? (
								<DevMenu customEntries={<SiteDevMenu />} />
							) : undefined
						}
						projectStorageKey={process.env.REACT_APP_SITE_KEY as string}
					>
						<SiteInterfaceProvider storageKey={process.env.REACT_APP_SITE_KEY}>
							<UserProvider>
								<GlobalComponents />

								{props.children}

								<Outlet />
								<ScrollRestoration />
							</UserProvider>
						</SiteInterfaceProvider>
					</FrameworkProvider>
				</AuthorisedUserProvider>
			</AuthProvider>
		</IPMReactQueryProvider>
	);
}

function ErrorMapper() {
	return (
		<Layout>
			<ErrorPage />
		</Layout>
	);
}

const appRouter = createBrowserRouter(
	[
		{
			element: <Layout />,
			errorElement: <ErrorMapper />,
			children: [
				...PublicRoutes,
				...SignUpRoutes,
				...TopRoutes,
				{
					element: (
						<NewMailboxProvider>
							<Outlet />
						</NewMailboxProvider>
					),
					children: [...MailRoutes],
				},
				{
					element: (
						<MessageCentreProvider>
							<Outlet />
						</MessageCentreProvider>
					),
					children: [...MessageCentreRoutes],
				},
			],
		},
	],
	{
		future: {
			v7_startTransition: true,
			v7_fetcherPersist: true,
			v7_normalizeFormMethod: true,
			v7_partialHydration: true,
			v7_skipActionErrorRevalidation: true,
		},
	},
);

function App() {
	return <RouterProvider router={appRouter} />;
}

export default App;
